/**
 * The product writes' REAL outcome, step by step, with the retry beside a
 * failure.
 *
 * Create Tenant used to mark "products recorded", "products switched on in
 * the workspace" and "Cluvik AI switched on" done whatever happened, and the
 * confirmation said "Tenant Created Successfully" regardless (audit A, Phase
 * 2 item 4, 6 Oct 2026). The server now decides each step by reading back the
 * collection the runtime reads, and sends `product_setup`:
 *
 *   { ok, result, failed: [step], steps: [{ step, label, status, detail,
 *     not_landed_labels }] }
 *
 * This renders it. A failed step reads as failed, names the products that did
 * not land BY NAME (the server sends names; nothing here prints a code), and
 * offers "Retry failed steps", which calls
 * POST /api/admin/tenants/{id}/product-setup/retry and shows ITS read-back.
 */
import React from 'react';
import { AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Button } from '../../../components/ui/button';

export function setupFailed(setup) {
  return !!setup && setup.ok === false;
}

export default function ProductSetupOutcome({ setup, onRetry, retrying, retryError }) {
  if (!setup || !Array.isArray(setup.steps) || setup.steps.length === 0) return null;
  const failed = setupFailed(setup);
  return (
    <section className={`rounded-xl border p-4 ${failed ? 'border-warning/30 bg-warning/10' : 'border-border bg-muted/20'}`}
             data-testid="product-setup-outcome">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        Product setup
      </p>
      <ul className="mt-2 space-y-2">
        {setup.steps.map((s) => {
          const ok = s.status === 'done';
          return (
            <li key={s.step} className="flex items-start gap-2" data-testid={`product-step-${s.step}`}>
              {ok ? (
                <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0 text-success" aria-hidden="true" />
              ) : (
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-warning-text" aria-hidden="true" />
              )}
              <div className="min-w-0">
                <p className="text-sm text-foreground">
                  {s.label}
                  <span className={`ml-2 text-xs font-medium ${ok ? 'text-success' : 'text-warning-text'}`}
                        data-testid={`product-step-status-${s.step}`}>
                    {ok ? 'Done' : 'Failed'}
                  </span>
                </p>
                {!ok && (s.not_landed_labels || []).length > 0 && (
                  <p className="text-xs text-foreground" data-testid={`product-step-products-${s.step}`}>
                    Not live: {s.not_landed_labels.join(', ')}
                  </p>
                )}
                {s.detail && (
                  <p className="text-xs text-muted-foreground">{s.detail}</p>
                )}
              </div>
            </li>
          );
        })}
      </ul>
      {failed && onRetry && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Button type="button" variant="outline" size="sm" onClick={onRetry}
                  disabled={retrying} data-testid="product-setup-retry">
            {retrying ? 'Retrying…' : 'Retry failed steps'}
          </Button>
          {retryError && (
            <span className="text-xs text-warning-text" data-testid="product-setup-retry-error">
              {retryError}
            </span>
          )}
        </div>
      )}
    </section>
  );
}
